import {
  CustomPermission,
  ParsedPermissions,
  PermissionsAction,
  PermissionsKey,
  PermissionsRaw,
  PermissionsWithIdKey,
  PermissionsWithIdsKey,
  RolePermissions,
  permissionsActions,
  permissionsKeys,
  permissionsWithIdKeys,
  permissionsWithIdsKeys,
} from "../domain/roles";

export const parsePermissions = (
  permissions: PermissionsRaw
): ParsedPermissions => {
  const result: RolePermissions = {
    organizations: {
      read: false,
      write: false,
    },
    organizations_notification_channel: {
      read: false,
      write: false,
    },
    organizations_display_setting: {
      write: false,
    },
    members: {
      read: false,
      write: false,
      delete: false,
    },
    roles: {
      read: false,
      write: false,
      delete: false,
    },
    version_categories: {
      read: false,
      write: false,
      delete: false,
    },
    algorithm_versions: {
      read: false,
    },
    frontend_addons: {
      read: false,
    },
    frontend_addons_owned: {
      read: false,
      write: false,
      delete: false,
    },
    endpoint_addons: {
      read: false,
    },
    view_masters: {
      read: false,
    },
    connect_masters: {
      read: false,
    },
    connects: {
      read: false,
      write: false,
      delete: false,
    },
    connect_jobs: {
      write: false,
    },
    connect_job_histories: {
      read: false,
    },
    connect_masters_owned: {
      read: false,
      write: false,
      delete: false,
    },
    optimize: {
      write: false,
    },
    evaluate: {
      write: false,
    },
    versions: {
      read: false,
      write: false,
      delete: false,
    },
    versions_meta: {
      write: false,
    },
    file_cell_large: {
      write: false,
    },
    tad_sheets: {
      read: false,
      write: false,
      delete: false,
    },
    tad_sheets_meta: {
      write: false,
    },
    version_category: { read: [], write: [], delete: [] },
    versions_version_category: { read: [], write: [], delete: [] },
    sheet: { read: [], write: [], delete: [] },
    version: { read: [], write: [], delete: [] },
    tad_sheet: { read: [], write: [], delete: [] },
    version_sheet: { read: [], write: [], delete: [] },
  };
  const customPermissions: CustomPermission[] = [];

  if (!permissions) {
    return { permissions: result, customPermissions };
  }
  for (const perm of permissions) {
    const strs = perm.split(":");
    let isKnownPermission = false;
    if (strs.length === 2) {
      const [permKey, action] = strs;
      if (
        permissionsActions.includes(action as PermissionsAction) &&
        permissionsKeys.includes(permKey as PermissionsKey)
      ) {
        isKnownPermission = true;
        setPermissions(
          result,
          permKey as PermissionsKey,
          action as PermissionsAction
        );
      }
    }
    if (strs.length === 3) {
      const [permKey, action, id] = strs;
      if (
        permissionsActions.includes(action as PermissionsAction) &&
        permissionsWithIdKeys.includes(permKey as PermissionsWithIdKey)
      ) {
        isKnownPermission = true;
        setPermissionsWithId(
          result,
          permKey as PermissionsWithIdKey,
          action as PermissionsAction,
          id
        );
      }
    }
    if (strs.length === 4) {
      const [permKey, action, versionId, sheetId] = strs;
      if (
        permissionsActions.includes(action as PermissionsAction) &&
        permissionsWithIdsKeys.includes(permKey as PermissionsWithIdsKey)
      ) {
        isKnownPermission = true;
        setVersionSheetPermissions(
          result,
          action as PermissionsAction,
          versionId,
          sheetId
        );
      }
    }
    if (!isKnownPermission) {
      const parsedCustomPermission = parseCustomPermissionRaw(perm);
      if (parsedCustomPermission) {
        customPermissions.push(parsedCustomPermission);
      }
    }
  }

  return { permissions: result, customPermissions };
};

export const parseCustomPermissionRaw = (
  rawPermission: string
): CustomPermission | null => {
  const parts = rawPermission.split(":").map((part) => part.trim());
  if (parts.length < 2 || parts.length > 4) return null;

  const [identifier, actionRaw, ...ids] = parts;
  if (!/^CP_[A-Za-z0-9_]+$/.test(identifier)) return null;

  const action = permissionsActions.includes(actionRaw as PermissionsAction)
    ? (actionRaw as PermissionsAction)
    : null;
  if (!action) return null;

  return {
    rawPermission,
    identifier,
    action,
    ids,
  };
};

const setPermissions = (
  result: RolePermissions,
  permissionsKey: PermissionsKey,
  action: PermissionsAction
) => {
  switch (action) {
    case "read":
      result[permissionsKey].read = true;
      break;
    case "write":
      result[permissionsKey].write = true;
      break;
    case "delete":
      result[permissionsKey].delete = true;
      break;
    default:
      throw new Error("不正なアクションが指定されました");
  }
};

const setPermissionsWithId = (
  result: RolePermissions,
  permissionsKey: PermissionsWithIdKey,
  action: (typeof permissionsActions)[number],
  id: string
) => {
  switch (action) {
    case "read":
      result[permissionsKey].read.push(id);
      break;
    case "write":
      result[permissionsKey].write.push(id);
      break;
    case "delete":
      result[permissionsKey].delete.push(id);
      break;
    default:
      throw new Error("不正なアクションが指定されました");
  }
};

const setVersionSheetPermissions = (
  result: RolePermissions,
  action: (typeof permissionsActions)[number],
  versionId: string,
  sheetId: string
) => {
  switch (action) {
    case "read":
      result.version_sheet.read.push({
        versionId,
        sheetId,
      });
      break;
    case "write":
      result.version_sheet.write.push({
        versionId,
        sheetId,
      });
      break;
    case "delete":
      result.version_sheet.delete.push({
        versionId,
        sheetId,
      });
      break;
    default:
      throw new Error("不正なアクションが指定されました");
  }
};

export const convertToPermissionsRaw = (
  permissions: RolePermissions
): PermissionsRaw => {
  const result: PermissionsRaw = [];
  for (const [permKey, permValue] of Object.entries(permissions)) {
    if (permissionsWithIdKeys.includes(permKey as PermissionsWithIdKey)) {
      for (const action of permissionsActions) {
        for (const id of permissions[permKey as PermissionsWithIdKey][action]) {
          result.push(`${permKey}:${action}:${id}`);
        }
      }
    } else if (
      permissionsWithIdsKeys.includes(permKey as PermissionsWithIdsKey)
    ) {
      for (const action of permissionsActions) {
        for (const { versionId, sheetId } of permissions[
          permKey as PermissionsWithIdsKey
        ][action]) {
          result.push(`${permKey}:${action}:${versionId}:${sheetId}`);
        }
      }
    } else {
      for (const [action, isPermitted] of Object.entries(permValue)) {
        if (isPermitted) {
          result.push(`${permKey}:${action}`);
        }
      }
    }
  }
  return result;
};
